import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Eye } from 'lucide-react';
import { branchDeposits, branchApproveDeposit, branchRejectDeposit, getBranchUser } from '../../services/branchApi';
import TxnDetailModal from './TxnDetailModal';

const statusColor: any = { approved: 'success', pending: 'warning', rejected: 'error' };
const money = (v: any) => (v == null ? '-' : `₹${Number(v).toLocaleString()}`);

export default function BranchDeposits() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<any | null>(null);
  const [action, setAction] = useState<{ row: any; kind: 'approve' | 'reject' } | null>(null);
  const [remark, setRemark] = useState('');
  const [busy, setBusy] = useState(false);
  // The Super Master and any agent/executive granted the deposit capability can act.
  const user = getBranchUser();
  const canAct = !!(user?.isManager || user?.canDeposit);

  const load = async () => {
    setLoading(true);
    try { setRows((await branchDeposits({ status })).data || []); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const submit = async () => {
    if (!remark.trim()) return;
    setBusy(true);
    try {
      const id = action!.row.deposit_id;
      if (action!.kind === 'approve') await branchApproveDeposit(id, remark.trim());
      else await branchRejectDeposit(id, remark.trim());
      setAction(null); setRemark(''); await load();
    } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };

  const cols = ['SN#', 'Requested', 'Client', 'Image', 'Ref ID', 'Amount', 'Approved', 'Remark', 'Action Date', 'Action By', 'Status', ...(canAct ? ['Actions'] : [])];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Manual Deposit{canAct ? '' : ' Report'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {canAct ? 'Approve or reject the deposit requests in your range' : 'All deposit requests for this branch'}
          </Typography>
        </Box>
        <TextField select size="small" label="Status" sx={{ minWidth: 160 }}
          value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
      </Stack>
      {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
        <Table size="small">
          <TableHead>
            <TableRow>{cols.map((h) => <TableCell key={h} sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.deposit_id} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell>{r.username}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setDetail(r)} title="View details & proof"><Eye size={16} /></IconButton>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.transaction_id || '-'}</TableCell>
                <TableCell>{money(r.amount)}</TableCell>
                <TableCell>{r.status === 'approved' ? money(r.approved_amount ?? r.amount) : money(0)}</TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.remark || ''}>{r.remark || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.handled_at ? new Date(r.handled_at).toLocaleString() : '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.action_by || '-'}</TableCell>
                <TableCell><Chip size="small" color={statusColor[r.status]} label={r.status} /></TableCell>
                {canAct && (
                  <TableCell>
                    {r.status === 'pending' && (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" color="success" onClick={() => { setAction({ row: r, kind: 'approve' }); setRemark(''); }}>Approve</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => { setAction({ row: r, kind: 'reject' }); setRemark(''); }}>Reject</Button>
                      </Stack>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>No deposits.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      <TxnDetailModal open={!!detail} onClose={() => setDetail(null)} kind="deposit" row={detail} />

      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{action?.kind === 'approve' ? 'Approve' : 'Reject'} deposit · {action?.row?.username}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {money(action?.row?.amount)} · {action?.kind === 'approve' ? 'the full amount will be credited to the user.' : 'no amount will be credited.'}
          </Typography>
          <TextField autoFocus fullWidth size="small" label="Remark (required)" value={remark}
            onChange={(e) => setRemark(e.target.value)} multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>Cancel</Button>
          <Button variant="contained" color={action?.kind === 'approve' ? 'success' : 'error'}
            disabled={busy || !remark.trim()} onClick={submit}>
            {busy ? 'Saving…' : action?.kind === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
